import React, { useRef } from 'react';
import { FiX, FiAward, FiDownload } from 'react-icons/fi';

function CertificateModal({ completion, onClose }) {
  const certRef = useRef(null);

  if (!completion) return null;

  const completedDate = new Date(completion.completed_at).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const handlePrint = () => {
    const printContent = certRef.current;
    const win = window.open('', '_blank');
    win.document.write(`
      <html>
        <head>
          <title>Certificate - ${completion.training_title || completion.trainingTitle}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { display: flex; align-items: center; justify-content: center; min-height: 100vh; background: white; }
            .cert-wrapper {
              width: 900px;
              padding: 60px;
              border: 8px double #3c79bb;
              border-radius: 12px;
              text-align: center;
              font-family: 'Georgia', serif;
              position: relative;
            }
            .cert-wrapper::before {
              content: '';
              position: absolute;
              inset: 8px;
              border: 2px solid #e5e7eb;
              border-radius: 8px;
              pointer-events: none;
            }
            .cert-icon { font-size: 48px; color: #3c79bb; margin-bottom: 16px; }
            .cert-heading { font-size: 14px; text-transform: uppercase; letter-spacing: 6px; color: #6b7280; margin-bottom: 8px; }
            .cert-title { font-size: 36px; font-weight: bold; color: #3c79bb; margin-bottom: 24px; }
            .cert-text { font-size: 16px; color: #4b5563; margin-bottom: 8px; }
            .cert-name { font-size: 32px; font-weight: bold; color: #111827; margin: 16px 0; border-bottom: 2px solid #3c79bb; display: inline-block; padding-bottom: 4px; }
            .cert-training { font-size: 24px; font-weight: bold; color: #3c79bb; margin: 16px 0; }
            .cert-date { font-size: 14px; color: #6b7280; margin-top: 24px; }
            .cert-id { font-size: 12px; color: #9ca3af; margin-top: 12px; }
            .cert-footer { margin-top: 40px; display: flex; justify-content: space-between; align-items: flex-end; }
            .cert-footer div { text-align: center; }
            .cert-line { width: 180px; border-top: 1px solid #9ca3af; margin-bottom: 4px; }
            .cert-footer-label { font-size: 12px; color: #6b7280; }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    win.document.close();
    setTimeout(() => {
      win.print();
      win.close();
    }, 500);
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 z-[60] animate-fadeIn"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto animate-slideUp"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal header */}
        <div className="flex items-center justify-between p-4 sm:p-5 border-b">
          <div className="flex items-center gap-3">
            <FiAward className="text-2xl text-[#3c79bb]" />
            <h2 className="text-lg sm:text-xl font-bold text-gray-900">Certificate of Completion</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 px-4 py-2 bg-[#3c79bb] text-white rounded-xl hover:bg-[#306196] transition-colors font-semibold text-sm"
            >
              <FiDownload className="text-base" />
              Print / Save
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-xl text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Certificate content */}
        <div className="p-6 sm:p-8">
          <div ref={certRef}>
            <div className="cert-wrapper border-[6px] border-double border-[#3c79bb] rounded-xl p-8 sm:p-12 text-center relative">
              {/* Inner border */}
              <div className="absolute inset-2 border-2 border-gray-200 rounded-lg pointer-events-none"></div>

              <div className="relative">
                {/* Award icon */}
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 rounded-full bg-[#3c79bb]/10 flex items-center justify-center">
                    <svg className="w-8 h-8 text-[#3c79bb]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                    </svg>
                  </div>
                </div>

                <p className="cert-heading text-xs sm:text-sm uppercase tracking-[6px] text-gray-500 mb-2">
                  Certificate of Completion
                </p>
                <h3 className="cert-title text-2xl sm:text-4xl font-bold text-[#3c79bb] mb-6 font-serif">
                  PW Connect
                </h3>

                <p className="cert-text text-gray-600 text-sm sm:text-base mb-2">
                  This is to certify that
                </p>
                <h4 className="cert-name text-xl sm:text-3xl font-bold text-gray-900 border-b-2 border-[#3c79bb] inline-block pb-1 mb-4">
                  {completion.user_name || completion.userName}
                </h4>

                <p className="cert-text text-gray-600 text-sm sm:text-base mb-2">
                  has successfully completed the training
                </p>
                <h4 className="cert-training text-lg sm:text-2xl font-bold text-[#3c79bb] mb-6">
                  {completion.training_title || completion.trainingTitle}
                </h4>

                <p className="cert-date text-sm text-gray-500">
                  Completed on {completedDate}
                </p>
                <p className="cert-id text-xs text-gray-400 mt-2">
                  Certificate ID: {completion.certificate_id || completion.certificateId}
                </p>

                {/* Footer with signature lines */}
                <div className="cert-footer mt-10 flex justify-between items-end px-8">
                  <div className="text-center">
                    <div className="cert-line w-40 border-t border-gray-400 mb-1 mx-auto"></div>
                    <p className="cert-footer-label text-xs text-gray-500">Date</p>
                  </div>
                  <div className="text-center">
                    <div className="cert-line w-40 border-t border-gray-400 mb-1 mx-auto"></div>
                    <p className="cert-footer-label text-xs text-gray-500">PW Connect Administrator</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CertificateModal;
